import { Component, Input, OnInit } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DistrictDto, SubDistrictDto } from '../../../../generated-model/models';
import { formatRecordedAt, resolveRecordedBy } from '../../../shared/commons/audit-display.util';
import { DistrictAutocompleteComponent } from '../../../shared/commons/district-autocomplete/district-autocomplete';

@Component({
  selector: 'app-subdistrict-modal',
  standalone: true,
  imports: [ReactiveFormsModule, DistrictAutocompleteComponent],
  templateUrl: './subdistrict-modal.html'
})
export class SubdistrictModal implements OnInit {
  @Input() subdistrict: SubDistrictDto | undefined;
  @Input() viewOnly = false;
  @Input() onSave?: (payload: any, isEditMode: boolean) => Promise<boolean>;

  form: FormGroup;
  districtDisplayText: string | null = null;
  isEditMode = false;
  submitted = false;
  isSubmitting = false;
  recordedByDisplay = '-';
  recordedAtDisplay = '-';

  constructor(public activeModal: NgbActiveModal, private fb: FormBuilder) {
    this.form = this.fb.group({
      id: [null],
      code: ['', [Validators.required, Validators.maxLength(6)]],
      title: ['', [Validators.required, Validators.maxLength(255)]],
      districtId: [null, [Validators.required]],
      ordering: [0, [Validators.max(32767)]],
      state: [1]
    });
  }

  ngOnInit(): void {
    if (this.subdistrict) {
      this.isEditMode = true;
      this.form.patchValue({
        ...this.subdistrict,
        districtId: this.subdistrict.district?.id ?? null,
      });
      this.districtDisplayText = this.subdistrict.district
        ? `${this.subdistrict.district.code ?? '-'} - ${this.subdistrict.district.title ?? ''}`.trim()
        : null;
      this.recordedByDisplay = resolveRecordedBy(this.subdistrict);
      this.recordedAtDisplay = formatRecordedAt(this.subdistrict);
    } else {
      this.form.reset({ ordering: 0, state: 1 });
      this.recordedByDisplay = '-';
      this.recordedAtDisplay = '-';
    }

    if (this.viewOnly) {
      this.form.disable();
    }
  }

  save(): void {
    if (this.viewOnly) {
      return;
    }
    this.submitted = true;
    if (this.form.invalid) return;
    const formValue = this.form.getRawValue();
    const payload: SubDistrictDto = {
      ...(this.subdistrict ?? {} as SubDistrictDto),
      ...formValue,
      district: {
        ...(this.subdistrict?.district ?? {} as DistrictDto),
        id: formValue.districtId,
      } as DistrictDto,
    };
    delete (payload as any).districtId;
    void this.submitAndClose(payload);
  }

  private async submitAndClose(payload: any): Promise<void> {
    if (!this.onSave) {
      this.activeModal.close(payload);
      return;
    }

    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    try {
      const isSuccess = await this.onSave(payload, this.isEditMode);
      if (isSuccess) {
        this.activeModal.close(payload);
      }
    } finally {
      this.isSubmitting = false;
    }
  }
}
